import React, { useMemo, useState, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Linking, StatusBar, Alert } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useAppSelector } from '@/store';
import { UserType } from '@/types/residents/user';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { FAQ_EXPAND_ICON_COLOR, FaqSection, faqSectionsGuarantor, faqSectionsResident } from '@/screens/residents/faq/faq';
import { strings } from '@/constants/residents';
import { ResidentMobileHeaderWithMenu } from '@/components/residents/mobile/ResidentMobileHeaderWithMenu';
import { MobileScreenLayout } from '@/components/residents/mobile/MobileScreenLayout';
import { LINKED_ACCOUNTS_COLORS } from '@/constants/residents/mobile/linkedAccountsColors';
import { useResidentScreen } from '@/hooks/residents/useResidentScreen';

const SUPPORT_EMAIL = 'support@resio.com';

const SectionRow = ({ section, index }: { section: FaqSection; index: number }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <View style={styles.sectionRow}>
      <TouchableOpacity
        style={styles.sectionHeaderRow}
        accessibilityRole="button"
        onPress={() => setExpanded(!expanded)}
        testID={`FAQ_Row_${index}_${expanded ? 'collapse' : 'expand'}`}
        accessibilityLabel={`FAQ_Row_${index}`}
      >
        <Text style={styles.sectionTitle}>{section.title}</Text>
        {expanded ? (
          <RESIDENTS_SVG_COMPONENTS.CIRCLE_MINUS width={24} height={24} color={FAQ_EXPAND_ICON_COLOR} />
        ) : (
          <RESIDENTS_SVG_COMPONENTS.CIRCLE_PLUS width={24} height={24} color={FAQ_EXPAND_ICON_COLOR} />
        )}
      </TouchableOpacity>
      {expanded && <Text style={styles.sectionSubtext}>{section.subtext}</Text>}
    </View>
  );
};

/**
 * Resident FAQ Screen
 * Uses new Figma design with gradient frame, dark theme, and accordion FAQ list
 * Following the same pattern as ResidentLinkedAccountsScreen
 */
export const ResidentFAQScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const { user } = useAppSelector((s) => s.auth);

  // Use resident screen hook for navigation handling
  const {
    handleNavigate,
    handleLogoPress,
    handleSignOut,
    handleNotificationPress,
  } = useResidentScreen('ResidentFAQ', navigation);

  const sections = useMemo<FaqSection[]>(() => {
    if (!user) return faqSectionsResident;
    return user.type === UserType.RESIDENT ? faqSectionsResident : faqSectionsGuarantor;
  }, [user]);

  const onSupportPress = useCallback(async () => {
    const mailtoUrl = `mailto:${SUPPORT_EMAIL}`;
    
    try {
      const canOpen = await Linking.canOpenURL(mailtoUrl);
      if (canOpen) {
        await Linking.openURL(mailtoUrl);
      } else {
        // Fallback: show alert with email address
        Alert.alert(
          'Contact Support',
          `Please email us at ${SUPPORT_EMAIL}`,
          [{ text: 'OK' }]
        );
      }
    } catch (error) {
      console.error('Failed to open email:', error);
      Alert.alert(
        'Contact Support',
        `Please email us at ${SUPPORT_EMAIL}`,
        [{ text: 'OK' }]
      );
    }
  }, []);

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" />
      
      {/* New Figma Header with Navigation Menu */}
      <ResidentMobileHeaderWithMenu
        onLogoPress={handleLogoPress}
        onSignOut={handleSignOut}
        onNavigate={handleNavigate}
        onNotificationPress={handleNotificationPress}
        currentPath="/faq"
        currentTitle="FAQ"
        showNotification={true}
      />
      
      <MobileScreenLayout
        backgroundColor={LINKED_ACCOUNTS_COLORS.background}
        contentPadding={20}
        contentPaddingBottom={24}
        contentGap={0}
        lightStatusBar={false}
      >
        {/* Header Card */}
        <View style={styles.headerCard}>
          <RESIDENTS_SVG_COMPONENTS.QUESTION_BUBBLE width={65} height={68} />
          <View style={styles.headerTextCol}>
            <Text style={styles.headerTitle}>{strings.faqCard.cardTitle}</Text>
            <Text style={styles.headerSubtitle}>{strings.faqCard.cardSubtitle}</Text>
            <Text style={styles.headerSubtitle}>{strings.faqCard.cardSubtitle2}</Text>
            <TouchableOpacity 
              accessibilityRole="button" 
              onPress={onSupportPress} 
              style={styles.supportButton} 
              testID="FAQ_GetInTouch"
            >
              <Text style={styles.supportButtonText}>{strings.faqCard.cardCta}</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* FAQ List */}
        <View style={styles.faqList}>
          {sections.map((section, idx) => (
            <SectionRow key={idx} section={section} index={idx} />
          ))}
        </View>
      </MobileScreenLayout>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: LINKED_ACCOUNTS_COLORS.cardBackground,
  },
  // Header Card - matches Figma "Contextual action" component
  headerCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: LINKED_ACCOUNTS_COLORS.cardBackground,
    borderRadius: 16,
    padding: 20,
    gap: 16,
    marginBottom: 24,
  },
  headerTextCol: {
    flex: 1,
    gap: 2,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    lineHeight: 27,
    marginBottom: 2,
  },
  headerSubtitle: {
    fontSize: 14,
    fontWeight: '400',
    color: LINKED_ACCOUNTS_COLORS.textMuted,
    lineHeight: 21,
  },
  supportButton: {
    marginTop: 20,
    alignSelf: 'flex-start',
    backgroundColor: LINKED_ACCOUNTS_COLORS.accentPink,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    height: 34,
    justifyContent: 'center',
    alignItems: 'center',
  },
  supportButtonText: {
    color: LINKED_ACCOUNTS_COLORS.background,
    fontWeight: '500',
    fontSize: 12,
    lineHeight: 18,
  },
  // FAQ List
  faqList: {
    // Container for FAQ items
  },
  sectionRow: {
    paddingVertical: 24,
    borderBottomWidth: 1,
    borderBottomColor: LINKED_ACCOUNTS_COLORS.divider,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 24,
  },
  sectionTitle: {
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    fontSize: 18,
    fontWeight: '600',
    flex: 1,
    lineHeight: 27,
  },
  sectionSubtext: {
    color: LINKED_ACCOUNTS_COLORS.textMuted,
    fontSize: 16,
    fontWeight: '400',
    marginTop: 10,
    lineHeight: 24,
  },
});

export default ResidentFAQScreen;
